import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useOrdersMain } from '../../hooks/useOrdersMain';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { CardList, Stats, OrdersAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import { StatusToolbar } from '../interaction/StatusToolbar';

export const Main = () => {
  const { t } = useTranslation('ordersShippedModule');
  const { getTeamType } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook que maneja toda la lógica de negocio
  const {
    // Estados
    selectedColumns,
    sorting,

    // Datos
    orders,
    isLoading,
    isFetching, // 🎯 Para aplicar opacidad durante refetch
    isFetchingNextPage, // 🎯 Para detectar infinite scroll
    error,
    total, // 🎯 Total de pedidos para detectar estado vacío

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,

    // Estadísticas
    ordersStats,
    statsLoading,

    // Conteos de estados logísticos
    logisticStatusCounts,
    countsLoading,

    // Props para componentes
    ordersViewProps,

    // Handlers
    handleShipmentStatusChange,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
    refetch,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchFilter,
    statusId,
    shipmentStatusId,
  } = useOrdersMain();

  // 🎯 Hook para manejar las columnas de AG Grid
  const { tanStackColumns, filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
    hasTeamData: isTenant3pl,
  });

  const [activeTab, setActiveTab] = useState('all');

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Función que decide que vista renderizar
  const renderOrders = () => {
    if (isMobile) {
      return <CardList {...ordersViewProps} showActions={true} selectedColumns={selectedColumns} />;
    }

    // 🎯 Usar AG Grid para la vista de escritorio (sin selección múltiple)
    return (
      <OrdersAgGridTable
        orders={orders}
        isFetching={isFetching}
        refetch={refetch}
        selectedColumns={selectedColumns}
        columnDefs={filteredColumnDefs}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Estadísticas */}
      <Stats data={ordersStats ?? null} isLoading={statsLoading} />

      {/* Header con filtros y botón crear */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              columns={tanStackColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              filterConditions={filterConditions}
              searchFilter={searchFilter}
              statusId={statusId}
              shipmentStatusId={shipmentStatusId}
              sorting={sorting}
            />
          </div>
        </div>
        <StatusToolbar
          onStatusChange={handleShipmentStatusChange}
          selectedCount={0} // 🎯 OrdersShipped no tiene selección
          onClearSelection={() => {}} // 🎯 No-op
          onCancelSelected={() => {}} // 🎯 No-op
          onPrepareSelected={() => {}} // 🎯 No-op
          onMarkForSendSelected={() => {}} // 🎯 No-op
          onUncancelSelected={() => {}} // 🎯 No-op
          cancelableCount={0}
          preparableCount={0}
          sendableCount={0}
          uncancelableCount={0}
          logisticStatusCounts={logisticStatusCounts}
          countsLoading={countsLoading}
          active={activeTab}
          setActive={setActiveTab}
        />
      </div>

      {/* 🎯 Estado vacío: solo cuando no hay datos Y no es carga inicial */}
      {!isInitialLoad && total === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
      )}

      {/* 🎯 Vista de pedidos: siempre en el DOM, controlado por opacity */}
      <div
        className={`mb-4 transition-opacity duration-200 ${
          total > 0 ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        } ${
          // Durante búsquedas (no primera carga): opacity-50
          !isInitialLoad && isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        {renderOrders()}
      </div>
    </div>
  );
};
